import {
  IconPlus,
  IconGripVertical,
} from '@tabler/icons-react';
import { Avatar, Badge, Box, Button, Card, Group, ScrollArea, Stack, Text, Title } from '@/components/ui';
import { mockTasks, mockProjects, mockUsers } from '../../mockData';

interface Column {
  id: string;
  title: string;
  color: string;
}

const columns: Column[] = [
  { id: 'todo', title: 'To Do', color: 'gray' },
  { id: 'in-progress', title: 'In Progress', color: 'blue' },
  { id: 'review', title: 'In Review', color: 'cyan' },
  { id: 'done', title: 'Done', color: 'green' },
];

const TaskCard = ({ task }: { task: typeof mockTasks[0] }) => {
  const project = mockProjects.find(p => p.id === task.projectId);
  const assignee = task.assigneeId ? mockUsers.find(u => u.id === task.assigneeId) : null;

  return (
    <Card p="sm" style={{ cursor: 'grab' }}>
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap={4}>
            <IconGripVertical size={14} color="var(--mantine-color-gray-5)" />
            <Text size="xs" c="dimmed">{project?.name}</Text>
          </Group>
          <Badge size="xs" color={
            task.priority === 'urgent' ? 'red' :
            task.priority === 'high' ? 'orange' :
            task.priority === 'medium' ? 'yellow' : 'gray'
          }>
            {task.priority}
          </Badge>
        </Group>

        <Text size="sm" fw={500}>{task.title}</Text>

        <Group gap="xs">
          {task.tags.slice(0, 2).map(tag => (
            <Badge key={tag} size="xs" variant="light">{tag}</Badge>
          ))}
        </Group>

        <Group justify="space-between">
          {task.dueDate && (
            <Text size="xs" c="dimmed">
              Due: {new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(task.dueDate)}
            </Text>
          )}
          {assignee && (
            <Avatar size="sm" radius="xl" color="blue">
              {assignee.name.split(' ').map(n => n[0]).join('')}
            </Avatar>
          )}
        </Group>
      </Stack>
    </Card>
  );
};

const KanbanColumn = ({ column, tasks }: { column: Column; tasks: typeof mockTasks }) => (
  <Box
    style={{
      minWidth: 300,
      maxWidth: 300,
      backgroundColor: 'var(--mantine-color-gray-light)',
      borderRadius: 12,
      padding: 12,
    }}
  >
    <Group justify="space-between" mb="md">
      <Group gap="sm">
        <Box
          w={12}
          h={12}
          style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${column.color}-filled)` }}
        />
        <Text fw={600}>{column.title}</Text>
        <Badge size="sm" variant="light" circle>
          {tasks.length}
        </Badge>
      </Group>
      <Button variant="subtle" size="xs" p={4}>
        <IconPlus size={14} />
      </Button>
    </Group>

    <Stack gap="sm">
      {tasks.map(task => (
        <TaskCard key={task.id} task={task} />
      ))}
      {tasks.length === 0 && (
        <Box ta="center" py="xl">
          <Text size="sm" c="dimmed">No tasks</Text>
        </Box>
      )}
    </Stack>
  </Box>
);

export default function BoardPage() {
  const getTasksByStatus = (status: string) => {
    return mockTasks.filter(t => t.status === status);
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Task Board</Title>
          <Text c="dimmed" size="sm">Drag and drop tasks between columns</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Add Task</Button>
      </Group>

      {/* Kanban Board */}
      <ScrollArea>
        <Group align="flex-start" gap="md" wrap="nowrap" pb="md">
          {columns.map(column => (
            <KanbanColumn
              key={column.id}
              column={column}
              tasks={getTasksByStatus(column.id)}
            />
          ))}
        </Group>
      </ScrollArea>
    </Stack>
  );
}
